import React, { useState } from 'react'
import { DocumentIcon, EyeIcon } from '@heroicons/react/24/outline'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import UploadShopDocumentModal from './UploadShopDocumentModal'
import DocumentStatus from '../documents/DocumentStatus'
import PDFViewer from '../common/PDFViewer'
import { SHOP_DOCUMENT_STATUSES } from '../../constants/shopDocumentStatuses'
import { formatTime } from '../../utils/date.util'

const DocumentsSection = ({ readOnly = false }) => {
  const { currentUser } = useAuth()
  const [selectedDocumentType, setSelectedDocumentType] = useState(null)
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false)

  const { data: documentsData } = useQuery({
    queryKey: QueryKeys.shopDocuments(currentUser?.userType),
    queryFn: () => handleGetRequest(`/${currentUser?.userType}s/shop-documents`),
    enabled: !!currentUser?.userType,
  })

  const { data: requiredDocumentsData } = useQuery({
    queryKey: QueryKeys.requiredDocuments(currentUser?.userType),
    queryFn: () => handleGetRequest(`/${currentUser?.userType}s/required-documents`),
    enabled: !!currentUser?.userType,
  })

  const documents = documentsData?.data || []
  const requiredDocuments = requiredDocumentsData?.data || []

  const getUploadedDocForType = (docType) => {
    return documents.find((doc) => doc.documentType?.id === docType.id)
  }

  const handleViewDocument = (document) => {
    if (document.filePath) {
      setSelectedDocument(document)
      setIsPDFViewerOpen(true)
    }
  }

  const handleClosePDFViewer = () => {
    setIsPDFViewerOpen(false)
    setSelectedDocument(null)
  }

  const renderRejectReason = (uploadedDoc) => {
    if (!uploadedDoc.rejectReason) return null
    if (uploadedDoc.status !== SHOP_DOCUMENT_STATUSES.rejected.key) return null

    return (
      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-700 text-sm">
          <strong>Rejection Reason:</strong> {uploadedDoc.rejectReason}
        </p>
      </div>
    )
  }

  const renderReupload = (uploadedDocument) => {
    if (readOnly) return
    if (uploadedDocument.status !== SHOP_DOCUMENT_STATUSES.rejected.key) return

    return (
      <button
        className="px-3 py-1 text-sm bg-neutral-200-100 hover:bg-neutral-200 rounded-md font-medium"
        onClick={() => setSelectedDocumentType(uploadedDocument.documentType)}
      >
        Re-upload
      </button>
    )
  }

  const renderDocument = (docType) => {
    const uploadedDoc = getUploadedDocForType(docType)

    if (!uploadedDoc) {
      return (
        <div key={docType.id} className="flex flex-col p-3 bg-neutral-200-50 rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DocumentIcon className="w-5 h-5 text-gray-600" />
              <div>
                <p className="text-sm font-medium text-gray-900">{docType.name}</p>
                <p className="text-xs text-gray-500">Not uploaded yet</p>
              </div>
            </div>

            <button
              className="px-3 py-1 text-sm bg-neutral-200-100 hover:bg-neutral-200 rounded-md font-medium"
              onClick={() => setSelectedDocumentType(docType)}
            >
              Upload
            </button>
          </div>
        </div>
      )
    }

    return (
      <div key={docType.id} className="flex flex-col p-3 bg-neutral-200-50 rounded-lg border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DocumentIcon className="w-5 h-5 text-gray-600" />
            <div>
              <p className="text-sm font-medium text-gray-900">{docType.name}</p>
              <p className="text-xs text-gray-500">
                PDF • Uploaded {formatTime(uploadedDoc.createdAt)}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <DocumentStatus status={uploadedDoc.status} />
            <button
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => handleViewDocument(uploadedDoc)}
              title="View document"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            {renderReupload(uploadedDoc)}
          </div>
        </div>

        {renderRejectReason(uploadedDoc)}
      </div>
    )
  }

  return (
    <div className="w-full">
      {requiredDocuments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <DocumentIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p>No documents required</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requiredDocuments.map((docType) => {
            return renderDocument(docType)
          })}
        </div>
      )}

      <UploadShopDocumentModal
        isOpen={!!selectedDocumentType}
        onClose={() => setSelectedDocumentType(null)}
        documentType={selectedDocumentType}
      />

      <PDFViewer
        isOpen={isPDFViewerOpen}
        onClose={handleClosePDFViewer}
        pdfUrl={selectedDocument?.filePath}
        documentName={selectedDocument?.documentType?.name || 'Document'}
      />
    </div>
  )
}

export default DocumentsSection
